import {useEffect, useState, useMemo, useContext, createContext} from 'react';
import {Conversation} from '@stripe-internal/conversations-client';
import {useConversationUIFeatureFlags} from '../FeatureFlagsProvider';

const TopAnchoredConfigContext = createContext({
  userDisabled: false,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setUserDisabled: (_: boolean) => {},
});

export function TopAnchoringConfigProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [userDisabled, setUserDisabled] = useState(false);

  const context = useMemo(
    () => ({userDisabled, setUserDisabled}),
    [userDisabled],
  );

  return (
    <TopAnchoredConfigContext.Provider value={context}>
      {children}
    </TopAnchoredConfigContext.Provider>
  );
}

export function useMessagesTopAnchored(conversation: Conversation): {
  hasTopAnchorFlag: boolean;
  shouldTopAnchor: boolean;
  setTopAnchoringEnabled: (enabled: boolean) => void;
} {
  const {userDisabled, setUserDisabled} = useContext(TopAnchoredConfigContext);

  const {newUniversalHelpDesign, messagesTopAnchored} =
    useConversationUIFeatureFlags();

  const [isChat, setIsChat] = useState(
    conversation.context.channel === 'chat' ||
      conversation.context.requestedChannel === 'chat',
  );

  useEffect(() => {
    const sub = conversation.contextObservable().subscribe(() => {
      setIsChat(
        conversation.context.channel === 'chat' ||
          conversation.context.requestedChannel === 'chat',
      );
    });
    return () => sub.unsubscribe();
  }, [conversation]);

  const hasTopAnchorFlag = !!(messagesTopAnchored && newUniversalHelpDesign);

  // We have to different types of "enabled": whether flag is on to
  // enable this feature entirely, or if we've manually turned it off.
  // We need both so in the case where we turn it off, we still keep
  // some of the structure in place to keep it working
  return {
    hasTopAnchorFlag,
    shouldTopAnchor: hasTopAnchorFlag && !isChat && !userDisabled,
    setTopAnchoringEnabled: (enabled: boolean) => setUserDisabled(!enabled),
  };
}
